import { useRouter } from 'expo-router';
import { CalendarCheck, ScanLine, ShieldCheck, Sparkles } from 'lucide-react-native';
import { Image, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';

/**
 * Onboarding landing page (Wave 58, user request) — the first screen a
 * brand-new install sees, BEFORE the questions start. One job: set
 * expectations in ten seconds (what you'll do, what you'll get, what
 * happens to your data) and hand momentum to "Build my plan". Mirrors
 * the homestead.ninja hero so the store → app transition feels
 * continuous. Existing users never see this — the boot check and the
 * onboarding gate only route here when no home exists yet.
 */
export default function Start() {
  const router = useRouter();

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}>
        <View style={styles.heroIconWrap}>
          <Image
            source={require('../../../assets/images/icon.png')}
            style={styles.heroIcon}
            resizeMode="contain"
          />
        </View>

        <Text style={styles.kicker}>WELCOME TO HOMESTEAD</Text>
        <Text style={styles.h1}>
          Every home should{'\n'}come with a <Text style={styles.h1Italic}>manual.</Text>
        </Text>
        <Text style={styles.sub}>
          Yours takes about two minutes to build. Here&apos;s how it works:
        </Text>

        <View style={styles.steps}>
          <StepRow
            Icon={Sparkles}
            tint="#EAF0EA"
            color={Palette.forest}
            title="Answer a few questions"
            body="Where the home is, how old, how it's heated. No account, no email."
          />
          <StepRow
            Icon={CalendarCheck}
            tint="#F3DDAE"
            color={Palette.honeyDeep}
            title="Get a year of maintenance, planned"
            body="Tuned to your climate — and you only ever see one thing at a time."
          />
          <StepRow
            Icon={ScanLine}
            tint="#DCE7E1"
            color={Palette.moss}
            title="Snap any paper, it files itself"
            body="Receipts, estimates, manuals, warranties — scanned, read, and organized."
          />
        </View>

        <Pressable
          onPress={() => router.push('/(onboarding)/welcome' as never)}
          style={({ pressed }) => [styles.cta, pressed && { transform: [{ scale: 0.98 }] }]}>
          <Text style={styles.ctaText}>Build my plan</Text>
        </Pressable>

        <View style={styles.privacyRow}>
          <ShieldCheck size={13} color={Palette.inkSoft} strokeWidth={2} />
          <Text style={styles.privacyText}>
            Free · No sign-up · Your data stays on your device
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

function StepRow({
  Icon,
  tint,
  color,
  title,
  body,
}: {
  Icon: typeof Sparkles;
  tint: string;
  color: string;
  title: string;
  body: string;
}) {
  return (
    <View style={styles.stepRow}>
      <View style={[styles.stepIcon, { backgroundColor: tint }]}>
        <Icon size={18} color={color} strokeWidth={1.9} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={styles.stepTitle}>{title}</Text>
        <Text style={styles.stepBody}>{body}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  scroll: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: Spacing.five,
    paddingVertical: Spacing.six,
  },
  heroIconWrap: { alignItems: 'center', marginBottom: Spacing.four },
  heroIcon: { width: 76, height: 76, borderRadius: 20 },
  kicker: {
    textAlign: 'center',
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    letterSpacing: 1.6,
    color: Palette.honeyDeep,
    marginBottom: 10,
  },
  h1: {
    textAlign: 'center',
    fontFamily: Fonts.serif,
    fontSize: 32,
    lineHeight: 38,
    color: Palette.ink,
    letterSpacing: -0.5,
  },
  h1Italic: { fontFamily: Fonts.serifItalic ?? Fonts.serif, fontStyle: 'italic' },
  sub: {
    textAlign: 'center',
    marginTop: 12,
    fontFamily: Fonts.sansMedium,
    fontSize: 14.5,
    lineHeight: 21,
    color: Palette.inkSoft,
  },
  steps: {
    marginTop: Spacing.five,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    paddingHorizontal: 16,
    paddingVertical: 6,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
    paddingVertical: 13,
  },
  stepIcon: {
    width: 38,
    height: 38,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
    letterSpacing: -0.1,
  },
  stepBody: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    lineHeight: 17,
    color: Palette.inkSoft,
  },
  cta: {
    marginTop: Spacing.five,
    backgroundColor: Palette.forest,
    borderRadius: 16,
    paddingVertical: 17,
    alignItems: 'center',
  },
  ctaText: {
    fontFamily: Fonts.sansBold,
    fontSize: 16.5,
    color: Palette.cream,
    letterSpacing: 0.2,
  },
  privacyRow: {
    marginTop: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  privacyText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
  },
});
